import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  cookieConsentService,
  yandexMetrikaService,
} from "@services";

const defaultContextValue = {
  consent: null,
  hasDecision: false,
  isBannerVisible: false,
  isSettingsOpen: false,
  isFunctionalEnabled: false,
  isAnalyticsEnabled: false,
  acceptAll: () => {},
  rejectOptional: () => {},
  savePreferences: () => {},
  openSettings: () => {},
  closeSettings: () => {},
};

export const CookieConsentContext = createContext(defaultContextValue);

export function useCookieConsent() {
  return useContext(CookieConsentContext);
}

function createInitialState() {
  const consent = cookieConsentService.getConsent();

  return {
    consent,
    isBannerVisible: consent === null,
    isSettingsOpen: false,
  };
}

export default function CookieConsentProvider({children}) {
  const [state, setState] = useState(createInitialState);

  useEffect(() => {
    if (state.consent && state.consent.analytics) {
      yandexMetrikaService.enable();
      return;
    }

    yandexMetrikaService.disable();
  }, [state.consent]);

  const saveConsent = useCallback(({functional, analytics}) => {
    const consent = cookieConsentService.saveConsent({
      functional,
      analytics,
    });

    setState({
      consent,
      isBannerVisible: false,
      isSettingsOpen: false,
    });

    return consent;
  }, []);

  const acceptAll = useCallback(() => {
    return saveConsent({
      functional: true,
      analytics: true,
    });
  }, [saveConsent]);

  const rejectOptional = useCallback(() => {
    return saveConsent({
      functional: false,
      analytics: false,
    });
  }, [saveConsent]);

  const savePreferences = useCallback((preferences) => {
    return saveConsent({
      functional: preferences.functional,
      analytics: preferences.analytics,
    });
  }, [saveConsent]);

  const openSettings = useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      isBannerVisible: false,
      isSettingsOpen: true,
    }));
  }, []);

  const closeSettings = useCallback(() => {
    setState((currentState) => ({
      ...currentState,
      isBannerVisible: currentState.consent === null,
      isSettingsOpen: false,
    }));
  }, []);

  const contextValue = useMemo(() => {
    const hasDecision = state.consent !== null;

    return {
      consent: state.consent,
      hasDecision,
      isBannerVisible: state.isBannerVisible,
      isSettingsOpen: state.isSettingsOpen,
      isFunctionalEnabled: Boolean(
        state.consent && state.consent.functional
      ),
      isAnalyticsEnabled: Boolean(
        state.consent && state.consent.analytics
      ),
      acceptAll,
      rejectOptional,
      savePreferences,
      openSettings,
      closeSettings,
    };
  }, [
    state,
    acceptAll,
    rejectOptional,
    savePreferences,
    openSettings,
    closeSettings,
  ]);

  return (
    <CookieConsentContext.Provider value={contextValue}>
      {children}
    </CookieConsentContext.Provider>
  );
}